import { useEffect, useMemo, useRef, forwardRef } from 'react';
import { Flex } from '@fastnorth/kraken/components/layout';
import { useResponsive } from '@fastnorth/kraken/components/page-layout';

type AdvancedFinisherCertificateProps = {
  content: string;
};

const AdvancedFinisherCertificate = forwardRef<HTMLDivElement, AdvancedFinisherCertificateProps>(
  ({ content }, forwardedRef) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const { isMobile, is2XL, isXL, isSM, isLG, isMD, isPortrait } = useResponsive();

    // React compares this prop by reference, so a new object would reassign innerHTML
    // on every render, discarding the DOM adjustments applied below.
    const html = useMemo(() => ({ __html: content }), [content]);

    useEffect(() => {
      const element = internalRef.current;
      if (!element) return;

      const paragraphs = element.querySelectorAll('p');

      paragraphs.forEach((p) => {
        const directChildren = Array.from(p.childNodes).filter(
          (node) => node.nodeType === Node.ELEMENT_NODE
        ) as HTMLElement[];

        const hasDirectImg = directChildren.some((child) => child.tagName.toLowerCase() === 'img');

        if (hasDirectImg) {
          p.classList.add('flex', 'justify-center', 'p-1');
          p.style.width = '100%';
        }
        // remove the powered by text
        if (p.getAttribute('data-f-id') === 'pbf') {
          p.remove();
        }
      });

      // Set the forwarded ref
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    }, [content, forwardedRef, isMobile, is2XL, isXL, isSM, isLG, isMD, isPortrait]); // re-run when the HTML changes

    return (
      <Flex
        id={'finisher-certificate-component'}
        ref={internalRef}
        className={'h-full w-full flex-col center justify-center align-middle p-4'}
        dangerouslySetInnerHTML={html}
      />
    );
  }
);

AdvancedFinisherCertificate.displayName = 'AdvancedFinisherCertificate';

export default AdvancedFinisherCertificate;
